/**
 * Column facts: the values a cell column holds, with source provenance — what the
 * terminal renderer reads (`FrameColumnFacts`). Calendar placement reads canonical values only.
 * Exact entries precede inexact entries, then UTC timepoints; source provenance is retained.
 */
import { DateTime, NaiveDate, type PartialDate, Utc } from "@tento-chrono";
import type { Option } from "../core/option.ts";
import type { CellEntry } from "../core/dtype.ts";
import type { EventItemAllDay, EventItemTimed } from "../indices/events/event-item.ts";
import type { RowNetworkKey } from "../ids/row-network-key.ts";
import { TASKS_FACADE, taskCalendarCellsInRange, taskCalendarMutationRevision, type TasksFacade } from "../facades/tasks-facade.ts";
import { taskCalendarBelongs } from "../indices/tasks-index-types.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import { SourceRow } from "../rows/variants/source-row.ts";
import type { ModelRow } from "../rows/model-row.ts";
import { ListDataSource } from "../rows/variants/list-data-source.ts";
import type { ColumnKey } from "./layout-types.ts";
import type { Timescale } from "./timescale.ts";

/**
 * Original temporal evidence and truthful storage origin beside display-period normalization.
 * `event`: an item of the calendar read (`CalendarEventRead`), its provenance the read's
 * (`CalendarEventRead.provenance`): a stored event, or an occurrence the recurrence expansion
 * generated (it has no cell; `storage` is the owning row's).
 */
export type SourceCellOrigin =
  & {
    /** The row store read here; derived cells do not claim an upstream physical owner. */
    readonly storage: "source" | "derived";
    readonly sourceRowId: RowLocalId;
    readonly backingOwnerKey: RowNetworkKey;
  }
  & (
    | { readonly kind: "date"; readonly cell: CellEntry }
    | { readonly kind: "timepoint"; readonly cell: CellEntry.Timepoint }
    | {
      readonly kind: "event";
      readonly item: EventItemTimed | EventItemAllDay;
      readonly occurrence: "stored" | "expanded";
    }
  );
/** The stored-cell origins `collectColumnFacts` places itself. */
type StoredCellOrigin = Exclude<SourceCellOrigin, { readonly kind: "event" }>;
/**
 * One value a cell column holds, with where it came from: the entry as the
 * column shows it (`entry`) and its evidence (`origin`: a stored cell of a
 * source row, or an item of the calendar read). The TUI's cells, `--explain`
 * and the parity export read these; `collectColumnFacts` collects them.
 */
export type ColumnFact = {
  readonly origin: SourceCellOrigin;
  readonly entry: CellEntry;
};
/**
 * A frame's column facts, for the TUI: row id, then column-fact key
 * (`columnFactKey`), then the facts of that cell.
 */
export type FrameColumnFacts = ReadonlyMap<RowLocalId, ReadonlyMap<string, readonly ColumnFact[]>>;
/** Captured only in the owning carried row/last frame; no global generation ledger. */
export type TaskCalendarMutations = readonly {
  readonly facade: TasksFacade;
  readonly revision: number;
}[];
/** Resolve the same owner walk as collection without reading any cell/index. */
export function captureTaskCalendarMutations(sources: readonly SourceRow[]): TaskCalendarMutations {
  const out: { facade: TasksFacade; revision: number }[] = [];
  for (const source of sources) for (const owner of taskCalendarOwners(source)) {
    const facade = owner.as(TASKS_FACADE);
    if (facade != null) out.push({ facade, revision: taskCalendarMutationRevision(facade) });
  }
  return out;
}
/** Identity catches owner replacement/exit/reentry; revisions survive a sibling's refresh. */
export function sameTaskCalendarMutations(
  previous: Option<TaskCalendarMutations>, current: TaskCalendarMutations,
): boolean {
  return previous != null && previous.length === current.length &&
    previous.every((held, i) => held.facade === current[i]?.facade && held.revision === current[i]?.revision);
}

/** Week identity and UTC bounds both matter: a zone switch cannot reuse stale facts. */
export function columnFactKey(column: ColumnKey): string {
  return column.pd.type + "|" + column.pd.start.dse + "|" + column.range.start.mse + "|" + column.range.end.mse;
}
export type ColumnProjectionDrops = {
  collapsed: number;
  noColumn: number;
  customNoColumn: number;
  unrenderedRow: number;
  total: number;
};
function periodKey(pd: PartialDate): string { return pd.type + "|" + pd.start.dse; }

export function collectColumnFacts(
  sources: readonly SourceRow[],
  columns: readonly ColumnKey[],
  colByPeriod: ReadonlyMap<string, ColumnKey>,
  range: NaiveDate.Range,
  timescale: Timescale,
  need: Option<ReadonlySet<ColumnKey>>,
  mode: "task-calendar" | "plain",
): { facts: Map<ColumnKey, ColumnFact[]>; dropped: ColumnProjectionDrops } {
  const out = new Map<ColumnKey, ColumnFact[]>();
  const dropped: ColumnProjectionDrops = { collapsed: 0, noColumn: 0, customNoColumn: 0, unrenderedRow: 0, total: 0 };
  for (const col of columns) if (need == null || need.has(col)) out.set(col, []);
  if (sources.length === 0) return { facts: out, dropped };
  const wanted = (col: ColumnKey): boolean => need == null || need.has(col);
  // Always keep facts; the return preserves the existing representative/drop accounting.
  const put = (col: ColumnKey, fact: ColumnFact): boolean => {
    const prior = out.get(col);
    if (prior != null) { const first = prior.length === 0; prior.push(fact); return first; }
    out.set(col, [fact]);
    return true;
  };

  // The span to read: the whole window, or (an incremental frame) the span
  // of the columns it re-reads.
  const needed = need == null ? columns : columns.filter((col) => need.has(col));
  const firstNeeded = needed[0];
  const lastNeeded = needed[needed.length - 1];
  if (firstNeeded == null || lastNeeded == null) return { facts: out, dropped };
  const read = need == null
    ? range
    : new NaiveDate.Range(firstNeeded.pd.start, lastNeeded.pd.end);

  // Identity is (unit, first day): the `yw-Y-W` string omits the week start,
  // so a Monday-keyed `yw-2026-17` would string-match the Sunday column that
  // begins six days later.
  const columnRank = unitRank(timescale);

  const dated: ColumnFact[] = [];
  const calendar: { origin: StoredCellOrigin; range: DateTime.Range<Utc> }[] = [];
  for (const source of sources) {
    if (mode === "task-calendar") {
      // Match the existing task provider's owner walk. A list's copied cells
      // neither supply physical origins nor merge TaskId winners across owners.
      for (const owner of taskCalendarOwners(source)) {
        const facade = owner.as(TASKS_FACADE);
        if (facade == null) continue;
        const needed = new DateTime.Range<Utc>(firstNeeded.range.start, lastNeeded.range.end);
        for (const held of taskCalendarCellsInRange(facade, needed)) {
          calendar.push({
            origin: { kind: "date", storage: owner.cellsDerived ? "derived" : "source",
              sourceRowId: held.sourceRowId, backingOwnerKey: held.backingOwnerKey, cell: held.cell },
            range: held.item.time,
          });
        }
      }
    } else {
      for (const entry of datedCellsMeeting(source, read)) {
        dated.push({ origin: { kind: "date", storage: source.cellsDerived ? "derived" : "source", sourceRowId: source.id, backingOwnerKey: source.backendRowKey(), cell: entry }, entry });
      }
    }
  }
  dated.sort((a, b) => a.entry.date.start.dse - b.entry.date.start.dse);
  const inexact: ColumnFact[] = [];
  for (const fact of dated) {
    const entry = fact.entry;
    const col = colByPeriod.get(periodKey(entry.date));
    if (col == null) {
      inexact.push(fact);
      continue;
    }
    if (!wanted(col)) continue;
    if (!put(col, fact)) dropped.collapsed++;
  }
  // Placement by binary search over the columns' starts (ascending): a finer
  // or equal cell goes to the one column containing its start, a coarser one
  // to every column it overlaps — O(log columns) a cell, not a column scan.
  for (const fact of inexact) {
    const entry = fact.entry;
    const first = firstColumnEndingAfter(columns, entry.date.start.dse);
    const targets: ColumnKey[] = [];
    if (unitRank(entry.date.type) > columnRank) {
      for (let i = first; i < columns.length; i++) {
        const col = columns[i];
        if (col == null || col.pd.start.dse >= entry.date.end.dse) break;
        targets.push(col);
      }
    } else {
      const col = columns[first];
      if (col != null && col.pd.start.dse <= entry.date.start.dse) targets.push(col);
    }
    if (targets.length === 0) {
      dropped.noColumn++;
      continue;
    }
    const mine = targets.filter(wanted);
    if (mine.length === 0) continue;
    let placed = false;
    for (const col of mine) placed = put(col, fact) || placed;
    if (!placed) dropped.collapsed++;
  }

  const utcFirst = need == null ? columns[0] : firstNeeded;
  const utcLast = need == null ? columns.at(-1) : lastNeeded;
  if (utcFirst != null && utcLast != null) {
    const utcRange = new DateTime.Range<Utc>(
      utcFirst.range.start,
      utcLast.range.end,
    );
    for (const source of mode === "task-calendar" ? [] : sources) {
      for (const cell of customCellsStartingIn(source, utcRange)) {
        calendar.push({
          origin: { kind: "timepoint", storage: source.cellsDerived ? "derived" : "source", sourceRowId: source.id, backingOwnerKey: source.backendRowKey(), cell },
          range: cell.dtr,
        });
      }
    }
    calendar.sort((a, b) => a.range.start.mse - b.range.start.mse);
    for (const candidate of calendar) {
      const targets = candidate.origin.kind === "date"
        ? columnsMeetingUtc(columns, candidate.range)
        : (() => { const col = columnContaining(columns, candidate.range.start); return col == null ? [] : [col]; })();
      const selected = targets.filter(wanted);
      // A declared calendar task outside the viewport is intentionally unplaced.
      if (targets.length === 0) {
        if (candidate.origin.kind === "timepoint") dropped.customNoColumn++;
        continue;
      }
      let placed = false;
      for (const col of selected) {
        const cell = candidate.origin.cell;
        const entry: CellEntry = { date: col.pd, id: cell.id, value: cell.value, style: cell.style };
        placed = put(col, { origin: candidate.origin, entry }) || placed;
      }
      if (selected.length > 0 && !placed) dropped.collapsed++;
    }
  }
  // Empty entries are a real replacement, not an instruction to retain old facts.
  for (const col of needed) if (!out.has(col)) out.set(col, []);

  dropped.total = dropped.noColumn + dropped.customNoColumn + dropped.unrenderedRow;
  return { facts: out, dropped };
}

/**
 * A source row's date-keyed cells whose period meets `range`. The store
 * filters by a cell's START, so a coarser cell that began before `range` (a
 * month cell under mid-month week columns) is read where such a cell can
 * start — the six days before (a week of any start day), the first of the
 * month and the first of the year — not across a year of days.
 */
function datedCellsMeeting(row: SourceRow, range: NaiveDate.Range): CellEntry[] {
  const out: CellEntry[] = [];
  const start = range.start;
  const monthStart = NaiveDate.fromYmd1Exp(start.yr, start.mth, 1);
  const yearStart = NaiveDate.fromYmd1Exp(start.yr, 1, 1);
  const reads = [new NaiveDate.Range(start.addDays(-6), range.end)];
  if (monthStart.dse < start.dse - 6) reads.push(new NaiveDate.Range(monthStart, monthStart.addDays(1)));
  if (yearStart.dse < start.dse - 6 && yearStart.dse !== monthStart.dse) {
    reads.push(new NaiveDate.Range(yearStart, yearStart.addDays(1)));
  }
  for (const r of reads) {
    for (const entry of row.peekDateCells(r)) {
      if (entry.date.end.dse > start.dse) out.push(entry);
    }
  }
  return out;
}

/** The first column (ascending starts) whose period ends after day `dse`; `columns.length` when none. */
function firstColumnEndingAfter(columns: readonly ColumnKey[], dse: number): number {
  let lo = 0;
  let hi = columns.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    const col = columns[mid];
    if (col != null && col.pd.end.dse <= dse) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** The column whose UTC range holds `at` (start inclusive, end exclusive), by binary search. */
function columnContaining(columns: readonly ColumnKey[], at: DateTime<Utc>): Option<ColumnKey> {
  let lo = 0;
  let hi = columns.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    const col = columns[mid];
    if (col != null && col.range.end.mse <= at.mse) lo = mid + 1;
    else hi = mid;
  }
  const col = columns[lo] ?? null;
  return col != null && col.range.containsExclusiveEnd(at) ? col : null;
}

/** Coarseness order of partial-date units (day < week < month < year). */
function unitRank(unit: Timescale): number {
  switch (unit) {
    case "day":
      return 0;
    case "week":
      return 1;
    case "month":
      return 2;
    case "year":
      return 3;
  }
}


function customCellsStartingIn(
  row: SourceRow,
  utcRange: DateTime.Range<Utc>,
): CellEntry.Timepoint[] {
  // A ranged read over the UTC days the span touches (the timepoint lane
  // matches whole UTC days, a superset of the span), then the exact span —
  // not every cell the row holds. The lane's start order is kept.
  const days = new NaiveDate.Range(utcRange.start.date, utcRange.end.date.addDays(1));
  return row.peekTimepointCells(days).filter((c) => utcRange.containsExclusiveEnd(c.dtr.start));
}



/** Binary admission plus actual overlap targets; O(log columns + output columns). */
function columnsMeetingUtc(columns: readonly ColumnKey[], span: DateTime.Range<Utc>): ColumnKey[] {
  let lo = 0, hi = columns.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    const col = columns[mid];
    if (col != null && col.range.end.mse <= span.start.mse) lo = mid + 1; else hi = mid;
  }
  const out: ColumnKey[] = [];
  for (let i = lo; i < columns.length; i++) {
    const col = columns[i];
    if (col == null || col.range.start.mse >= span.end.mse) break;
    if (taskCalendarBelongs(span, col.range)) out.push(col);
  }
  return out;
}

/** The same per-owner list traversal used by TasksDataProvider.collectTasks. */
function taskCalendarOwners(row: ModelRow): readonly SourceRow[] {
  if (row instanceof ListDataSource) return row.constituentRows().flatMap(taskCalendarOwners);
  if (row instanceof SourceRow && row.as(TASKS_FACADE) != null) return [row];
  return row.children.flatMap(taskCalendarOwners);
}
